import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AreaChart, Area, XAxis, YAxis, ReferenceLine, Tooltip, ResponsiveContainer } from "recharts";

/**
 * FlowRateTrend — compact 12-hour sparkline of manual flow-rate inputs for a vessel.
 * Sits underneath the FeedingRateGauge in the dashboard gauge card.
 *  - Polls every 60 s (cheap query — last-12h slice).
 *  - Renders empty state when no inputs in window.
 *  - Target line at 1250 t/h (red dashed) for at-a-glance benchmarking.
 */
const TARGET = 1250;
const MAX = 1500;
const POLL_MS = 60_000;

function fmtHm(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}

export default function FlowRateTrend({ vesselId, hours = 12 }) {
    const [points, setPoints] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get(`/vessels/${vesselId}/flow-rate`, {
                    params: { since_hours: hours },
                });
                if (cancelled) return;
                setPoints(
                    (data || []).map((d) => ({
                        t: fmtHm(d.created_at),
                        rate: Math.round(d.flow_rate_tph),
                        full: d,
                    }))
                );
            } catch {
                /* ignore */
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        const t = setInterval(load, POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(t);
        };
    }, [vesselId, hours]);

    return (
        <div className="mt-5 pt-4 border-t border-slate-100" data-testid="flow-rate-trend">
            <div className="flex items-baseline justify-between mb-1.5">
                <div className="uppercase-label text-[var(--grindrod-red)]">
                    Flow-rate trend · last {hours}h
                </div>
                <div className="text-[10px] text-slate-500 tabular">
                    {points.length} input{points.length === 1 ? "" : "s"}
                </div>
            </div>
            {loading ? (
                <div className="h-[72px] flex items-center justify-center text-xs text-slate-400" data-testid="flow-trend-loading">
                    Loading trend…
                </div>
            ) : points.length < 2 ? (
                <div
                    className="h-[72px] flex items-center justify-center text-xs text-slate-400 border border-dashed border-slate-200 rounded-sm"
                    data-testid="flow-trend-empty"
                >
                    {points.length === 0
                        ? "No flow-rate updates in the last 12 hours."
                        : "Need at least 2 inputs to draw a trend."}
                </div>
            ) : (
                <div style={{ height: 72 }} data-testid="flow-trend-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={points} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                            <defs>
                                <linearGradient id="frt-grad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#0B1B3D" stopOpacity={0.35} />
                                    <stop offset="100%" stopColor="#0B1B3D" stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="t" stroke="#94A3B8" fontSize={9} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                            <YAxis domain={[0, MAX]} stroke="#94A3B8" fontSize={9} tickLine={false} axisLine={false} tickCount={3} />
                            <Tooltip
                                contentStyle={{ borderRadius: 2, border: "1px solid #CBD5E1", fontSize: 11 }}
                                labelFormatter={(t) => `at ${t}`}
                                formatter={(v) => [`${v.toLocaleString()} t/h`, "Flow rate"]}
                            />
                            <ReferenceLine
                                y={TARGET}
                                stroke="#E3000F"
                                strokeDasharray="3 2"
                                strokeWidth={1}
                                label={{ value: "Target", position: "right", fill: "#E3000F", fontSize: 9 }}
                            />
                            <Area
                                type="monotone"
                                dataKey="rate"
                                stroke="#0B1B3D"
                                strokeWidth={1.8}
                                fill="url(#frt-grad)"
                                dot={{ r: 2, fill: "#0B1B3D" }}
                                activeDot={{ r: 4 }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}
